import React from 'react';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LogoIconProps {
  size?: number;
  className?: string;
}

interface LogoFullProps {
  className?: string;
  iconSize?: number;
}

// ─── LogoIcon ────────────────────────────────────────────────────────────────

/**
 * Nova Cafe monogram — concept 03.
 *
 * A geometric N: two 4-wide stems with a diagonal weighted heavier than them,
 * falling left-to-right like a pour. Pure letterform — no cup, no tile, no
 * gradient, which is what lets it sit in a near black-and-white system.
 *
 * Anatomy (source grid is 32×32; see src/features/design-preview):
 *   stems    — rect x=7 and x=21, y 6→26, width 4
 *   diagonal — 6.1 horizontal ≈ 5.0 perpendicular on the 14:20 slope,
 *              so it reads heavier than the 4-wide stems
 *
 * The viewBox crops to "5 5 22 22" rather than the full 32×32 grid: the glyph
 * is 18×20, so the untrimmed grid wasted ~30% of the box as padding. Cropping
 * gives the mark real presence at 40px and more pixels at favicon size. The
 * geometry is unchanged — only the frame is tighter.
 *
 * Paints with `currentColor`, so it inherits from its container and needs no
 * separate light/dark variant.
 */
export const LogoIcon: React.FC<LogoIconProps> = ({ size = 40, className }) => {
  const { t } = useSafeTranslation();

  return (
  <svg
    width={size}
    height={size}
    viewBox="5 5 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={t('brand.name')}
    className={className}
  >
    <g fill="currentColor">
      <rect x="7" y="6" width="4" height="20" />
      <rect x="21" y="6" width="4" height="20" />
      <path d="M11 6h6.1L25 26h-6.1L11 6Z" />
    </g>
  </svg>
  );
};

// ─── LogoFull ────────────────────────────────────────────────────────────────

/**
 * Monogram + "Nova Cafe" wordmark + "Platform" subtext, horizontal layout.
 * Use in: Sidebar header, Dashboard header, onboarding screens.
 */
export const LogoFull: React.FC<LogoFullProps> = ({ className, iconSize = 40 }) => {
  const { t } = useSafeTranslation();

  return (
  <div className={`flex items-center gap-2.5 ${className ?? ''}`}>
    <LogoIcon size={iconSize} className="text-neutral-900 flex-shrink-0" />
    <div className="flex flex-col leading-none">
      <span className="text-lg font-bold tracking-tight text-neutral-900 leading-tight">
        {t('brand.name')}
      </span>
      <span className="text-xs font-medium uppercase tracking-label text-neutral-400 mt-0.5">
        {t('brand.platform')}
      </span>
    </div>
  </div>
  );
};

// ─── LogoWhite ───────────────────────────────────────────────────────────────

/**
 * Same as LogoFull but inverted — for dark surfaces.
 */
export const LogoWhite: React.FC<LogoFullProps> = ({ className, iconSize = 40 }) => {
  const { t } = useSafeTranslation();

  return (
  <div className={`flex items-center gap-2.5 ${className ?? ''}`}>
    <LogoIcon size={iconSize} className="text-white flex-shrink-0" />
    <div className="flex flex-col leading-none">
      <span className="text-lg font-bold tracking-tight text-white leading-tight">
        {t('brand.name')}
      </span>
      <span className="text-xs font-medium uppercase tracking-label text-white/60 mt-0.5">
        {t('brand.platform')}
      </span>
    </div>
  </div>
  );
};
